import { faBriefcase, faClock } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { clsx } from 'clsx'
import { useDebouncedCallback } from 'use-debounce'
import { useOrderApplicantStatusMutation } from '~/api/mutations/order'
import type { OrderInfosResponse } from '~/api/queries/order'
import { EditorInput } from '~/components/Form'
import { Dropdown } from '~/components/Form/Input/Dropdown'
import { Badge } from '~/components/Global/Blocks/Badge/Badge'
import { acceptModal } from '~/components/Scopes/Orders/Modals/modals'
import type { ApplicantHeadData } from '~/domains/applicants/types'
import { useTaxonomy } from '~/domains/taxonomies/queries/useTaxonomy'
import { formatRelativeTime } from '~/utils'
import {
  applicantModalAsideSection,
  applicantModalAsideSectionTitle,
  applicantModalAsideSectionTitleOrder,
  applicantModalOrderNote,
  applicantModalOrderTitle,
  applicantModalOrderUpdater,
  applicantModalOrderUpdaterDropdown,
  applicantModalOrderUpdaterUpdatedAt
} from '../ApplicantModal.css'

type OrderStatusUpdaterProps = {
  applicant: ApplicantHeadData
  order: OrderInfosResponse
  applicantStatus: {
    id: number | null
    updatedAt: string | null
    note: string | null
  }
  onStatusChange: (status: number) => void
}

export const OrderStatusUpdater = ({
  applicant,
  order,
  applicantStatus,
  onStatusChange
}: OrderStatusUpdaterProps) => {
  const { data: orderStatuses } = useTaxonomy('applicant_order_statuses')
  const { mutate: updateApplicantOrderStatusMutate, isPending } =
    useOrderApplicantStatusMutation(order.id)

  const changeOrderStatus = (toStatusId: number) => {
    if (!toStatusId || applicantStatus.id === undefined || !order) {
      return
    }

    if (applicantStatus.id === toStatusId) {
      return
    }

    if (
      orderStatuses?.find((status) => status.id === toStatusId)
        ?.specialStatus === 'accepted'
    ) {
      acceptModal(order, [applicant], toStatusId).then((success) => {
        if (!success) {
          return
        }
        onStatusChange(toStatusId)
      })
      return
    }

    updateApplicantOrderStatusMutate(
      {
        applicants: [applicant],
        status: toStatusId
      },
      {
        onSuccess: () => {
          onStatusChange(toStatusId)
        }
      }
    )
  }

  const orderStatusNoteChange = useDebouncedCallback((note: string) => {
    if (applicantStatus.id === null || applicantStatus.id === undefined) {
      return
    }
    updateApplicantOrderStatusMutate({
      applicants: [applicant],
      status: applicantStatus.id,
      note
    })
  }, 500)

  const isSpecialStatusAccepted =
    orderStatuses?.find((status) => status.id === applicantStatus.id)
      ?.specialStatus === 'accepted'

  return (
    <div className={applicantModalAsideSection}>
      <div
        className={clsx(
          applicantModalAsideSectionTitle,
          applicantModalAsideSectionTitleOrder
        )}
      >
        <div>
          <FontAwesomeIcon icon={faBriefcase} /> Commande actuelle{' '}
          {order.status === 0 ? '(fermée)' : ''}
        </div>
        <div className={applicantModalOrderTitle}>
          {order.company.name} - {order.jobTitle}
          {order.qualifications.length > 0
            ? `(${order.qualifications.map((q) => q.name).join(', ')})`
            : ''}
        </div>
      </div>
      <div className={applicantModalOrderUpdater}>
        <Dropdown
          wrapperClassName={applicantModalOrderUpdaterDropdown}
          options={
            orderStatuses?.map((status) => ({
              label: status.name,
              value: String(status.id),
              element: (
                <Badge title={status.name} backgroundColor={status.color} />
              )
            })) ?? []
          }
          required
          small
          disabled={
            order.status === 0
              ? 'La commande est fermée, vous ne pouvez plus modifier le statut du candidat'
              : isSpecialStatusAccepted
                ? 'Le statut accepté est définitif'
                : false
          }
          placeholder="Changer le statut sur la commande"
          defaultValueOption={
            orderStatuses
              ?.filter((status) => status.id === applicantStatus.id)
              .map((status) => ({
                label: status.name,
                value: String(status.id),
                element: (
                  <Badge title={status.name} backgroundColor={status.color} />
                )
              }))[0]
          }
          onChange={(value) => changeOrderStatus(Number(value))}
          isUpdating={isPending}
        />
        {applicantStatus.updatedAt &&
          !Number.isNaN(Date.parse(applicantStatus.updatedAt)) && (
            <span className={applicantModalOrderUpdaterUpdatedAt}>
              <FontAwesomeIcon icon={faClock} />{' '}
              {formatRelativeTime(new Date(applicantStatus.updatedAt))}
            </span>
          )}
      </div>
      {applicantStatus?.id && (
        <div className={applicantModalOrderNote}>
          <EditorInput
            label=""
            name="orderStatusNote"
            content={applicantStatus?.note ?? ''}
            onChange={(value) => orderStatusNoteChange(value as string)}
            showBottomMenu={false}
            isReduced
            autofocus={false}
            placeholder="Saisir un commentaire sur la commande..."
          />
        </div>
      )}
    </div>
  )
}
